import { Grid } from '@mui/material';
import { BarChart as MuiBarChart } from '@mui/x-charts';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableView } from '../../../../tableview/TableView';
import { useElementWidth } from '../../common/utils';
import { ChartHeader } from '../common/chart-header/ChartHeader';
import { AxisConfigType, BarChartLayouts } from '../common/common-chart-types';
import {
  getChartLegendConfig,
  getChartNoDataOverlay,
} from '../common/common-chart-utils/common-chart-configs';
import {
  CHART_CONTENT_HEIGHT,
  defaultChartColors,
} from '../common/common-chart-utils/common-chart-styles';
import { getBarChartTableDataFromChartData } from '../common/common-chart-utils/common-chart-utils';
import {
  getMarginsBarChart,
  getModifiedXAxisConfigsBarChart,
} from './bar-chart-display-utils';
import { barChartStyles } from './bar-chart-styles';
import { BarChartProps } from './bar-chart-types';
import {
  getBarLabelConfig,
  getMuiBarChartDataConfigFromChartData,
} from './bar-chart-utils';

/**
 * Horizontal bar chart component - renders bars horizontally with categories on Y-axis
 * and values on X-axis.
 *
 * Note: This is a separate component to avoid breaking the standard BarChart.
 * Margins and label handling may be suboptimal for horizontal layout.
 *
 * TODO: Eventually combine with BarChart by adding an `isHorizontal` prop and handling
 * the axis swap + margin calculations properly for both orientations.
 */
export const HorizontalBarChart = (props: BarChartProps) => {
  const {
    fullScreenHandle,
    titleConfig,
    displayConfig,
    chartData,
    chartColors = defaultChartColors,
  } = props;
  const selectedSortOrder = displayConfig.enableSortOrder
    ? (displayConfig.selectedSortOrder ?? null)
    : null;
  const {
    showLabels = false,
    showTableView = false,
    showLegend = false,
    showGridLines = false,
    layout = BarChartLayouts.GROUPED,
  } = displayConfig;
  const showPercentage = layout === BarChartLayouts.PERCENTAGE;
  const chartDataConfig = getMuiBarChartDataConfigFromChartData(chartData, {
    layout,
    selectedSortOrder,
  });
  const tableData = getBarChartTableDataFromChartData(chartData, {
    layout,
    selectedSortOrder,
  });
  const { xAxisConfigs, yAxisConfigs, series, dataset } = chartDataConfig;
  const { t } = useTranslation();

  const barLabelConfig = getBarLabelConfig({
    series,
    showPercentage,
  });

  const isStackable =
    chartData.type === 'multi-dataset' && chartData.isStackable;
  const modifiedDisplayConfig = {
    ...displayConfig,
    enablePercentage: isStackable,
    enableStack: isStackable,
  };

  const [showTools, setShowTools] = useState(false);
  const { elementRef, elementWidth } = useElementWidth();
  const chartRef = useRef<HTMLDivElement>(null);
  const xAxisConfigsInput = {
    isHorizontal: true as const,
    xAxisConfigs: xAxisConfigs,
  };

  const modifiedXAxisConfigs = getModifiedXAxisConfigsBarChart(
    xAxisConfigsInput,
    dataset,
    elementWidth ?? 0
  );

  // Category axis: band scale with labels (for horizontal: Y-axis)
  // Extract category labels from dataset for colorMap
  const categoryLabels = (dataset ?? []).map(
    (d) => d[modifiedXAxisConfigs.firstAxisConfig.dataKey as string] as string
  );

  // Create colorMap to assign different colors to each category
  // See: https://mui.com/x/react-charts/styling/#ordinal-color-map
  const categoryColorMap = {
    type: 'ordinal' as const,
    values: categoryLabels,
    colors: categoryLabels.map(
      (_, index) => chartColors[index % chartColors.length]
    ),
  };

  const categoryAxisWithColorMap =
    modifiedXAxisConfigs.type === AxisConfigType.SINGLE
      ? [
          {
            ...modifiedXAxisConfigs.firstAxisConfig,
            colorMap: categoryColorMap,
          },
        ]
      : [
          {
            ...modifiedXAxisConfigs.firstAxisConfig,
            colorMap: categoryColorMap,
          },
          modifiedXAxisConfigs.secondAxisConfig,
        ];

  const muiCategoryAxis = categoryAxisWithColorMap;

  // Value axis: numeric scale with values (for horizontal: X-axis)
  const muiValueAxis = yAxisConfigs
    ? yAxisConfigs.type === AxisConfigType.SINGLE
      ? [yAxisConfigs.firstAxisConfig]
      : [yAxisConfigs.firstAxisConfig, yAxisConfigs.secondAxisConfig]
    : undefined;

  const baseMargins = getMarginsBarChart(
    modifiedXAxisConfigs,
    yAxisConfigs,
    series,
    dataset,
    elementWidth ?? 0,
    showLegend
  );

  // For horizontal charts, we need more left margin to accommodate Y-axis labels
  const margins = {
    ...baseMargins,
    left: Math.max(baseMargins.left, 100), // Ensure enough space for category labels
  };

  return (
    <Grid
      item
      container
      ref={elementRef}
      direction="column"
      sx={{ height: '100%' }}
      onMouseEnter={() => setShowTools(true)}
      onMouseLeave={() => setShowTools(false)}
    >
      <ChartHeader
        titleConfig={titleConfig}
        showTools={showTools}
        chartRef={chartRef}
        fullScreenHandle={fullScreenHandle}
        displayConfig={modifiedDisplayConfig}
      />
      <Grid item container sx={{ height: CHART_CONTENT_HEIGHT }}>
        {showTableView ? (
          <TableView
            tableData={tableData}
            fullScreenHandle={fullScreenHandle}
            chartHeight={CHART_CONTENT_HEIGHT}
          />
        ) : (
          <div ref={chartRef} style={{ width: '100%', height: '100%' }}>
            <MuiBarChart
              sx={barChartStyles()}
              title={titleConfig.mainTitle}
              // For horizontal: categories on Y, values on X
              xAxis={muiValueAxis as never}
              yAxis={muiCategoryAxis as never}
              dataset={dataset}
              series={series}
              margin={margins}
              layout="horizontal"
              slotProps={{
                popper: { container: () => fullScreenHandle.node.current },
                legend: getChartLegendConfig(showLegend),
                noDataOverlay: getChartNoDataOverlay(t),
              }}
              barLabel={showLabels ? barLabelConfig : undefined}
              grid={{
                vertical: showGridLines,
                horizontal: showGridLines,
              }}
              tooltip={{ trigger: 'axis' }}
            />
          </div>
        )}
      </Grid>
    </Grid>
  );
};
